import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { BadgeVariant } from '../../components/common/Badge';
import { Badge } from '../../components/common/Badge';
import { EmptyState } from '../../components/common/EmptyState';
import { SkeletonTable } from '../../components/common/SkeletonTable';
import { apiClient } from '../../services/api';
import type { ScheduledJobDto } from '../../types';
import { formatCountdown, formatDate, formatRelativeTime } from '../../utils/dateFormat';

function getFunctionAppVariant(functionApp: string): BadgeVariant {
  const normalized = functionApp.trim().toLowerCase();
  if (normalized === 'processing') return 'info';
  if (normalized === 'discovery') return 'purple';
  if (normalized === 'ingestion') return 'success';
  return 'neutral';
}

function getStatusVariant(status: ScheduledJobDto['status']): BadgeVariant {
  if (status === 'Active') return 'success';
  if (status === 'Running') return 'info';
  if (status === 'Failed') return 'error';
  return 'warning';
}

function normalizeTriggerType(triggerType: ScheduledJobDto['triggerType'] | string | undefined): string {
  return (triggerType ?? '').trim().toLowerCase();
}

function getTriggerTypeLabel(job: ScheduledJobDto): string {
  const normalized = normalizeTriggerType(job.triggerType);
  if (normalized.includes('timer')) return 'Timer';
  if (normalized === 'queue' || normalized === 'servicebus' || normalized === 'service bus') return 'Queue';
  if (normalized === 'http') return 'HTTP';
  return job.triggerType || 'Unknown';
}

function getCategory(job: ScheduledJobDto): 'Timer' | 'Queue' | 'HTTP' {
  if (job.category === 'Timer' || job.category === 'Queue' || job.category === 'HTTP') {
    return job.category;
  }

  const triggerType = getTriggerTypeLabel(job);
  if (triggerType === 'Timer') return 'Timer';
  if (triggerType === 'Queue') return 'Queue';
  return 'HTTP';
}

function isTimerTrigger(job: ScheduledJobDto): boolean {
  return getCategory(job) === 'Timer';
}

function isQueueTrigger(job: ScheduledJobDto): boolean {
  return getCategory(job) === 'Queue';
}

function isHttpTrigger(job: ScheduledJobDto): boolean {
  return getCategory(job) === 'HTTP';
}

function JobsTableSection({ title, jobs }: { title: string; jobs: ScheduledJobDto[] }) {
  return (
    <section className="card mt-4">
      <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
        {title} ({jobs.length})
      </h2>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <caption className="sr-only">{title}</caption>
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className="px-3 py-3 font-semibold">Job Name</th>
              <th className="px-3 py-3 font-semibold">Trigger Type</th>
              <th className="px-3 py-3 font-semibold">Last Run</th>
              <th className="px-3 py-3 font-semibold">Next Run</th>
              <th className="px-3 py-3 font-semibold">Status</th>
              <th className="px-3 py-3 font-semibold">Function App</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-800 dark:bg-gray-900">
            {jobs.map((job) => (
              <tr key={`${job.functionApp}-${job.name}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="px-3 py-3">
                  <p className="font-semibold text-gray-900 dark:text-gray-100">{job.name}</p>
                </td>
                <td className="px-3 py-3">
                  {getTriggerTypeLabel(job)}
                </td>
                <td className="px-3 py-3">
                  {job.lastRun ? (
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{formatRelativeTime(job.lastRun)}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{formatDate(job.lastRun, 'PPpp')}</p>
                    </div>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-3 py-3">
                  {job.nextRun ? (
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{formatCountdown(job.nextRun)}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{formatDate(job.nextRun, 'PPpp')}</p>
                    </div>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-3 py-3">
                  <Badge size="sm" variant={getStatusVariant(job.status)}>
                    {job.status}
                  </Badge>
                </td>
                <td className="px-3 py-3">
                  <Badge size="sm" variant={getFunctionAppVariant(job.functionApp)}>
                    {job.functionApp}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function JobsPage() {
  const AUTO_REFRESH_INTERVAL_SECONDS = 30;
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const {
    data: jobs = [],
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  } = useQuery<ScheduledJobDto[], Error>({
    queryKey: ['scheduledJobs'],
    queryFn: async () => (await apiClient.getScheduledJobs()) as ScheduledJobDto[],
    refetchInterval: autoRefreshEnabled ? AUTO_REFRESH_INTERVAL_SECONDS * 1000 : false,
  });

  const timerJobs = useMemo(() => jobs.filter(isTimerTrigger), [jobs]);
  const queueJobs = useMemo(() => jobs.filter(isQueueTrigger), [jobs]);
  const httpJobs = useMemo(() => jobs.filter(isHttpTrigger), [jobs]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Scheduled Jobs</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Live visibility into timer, queue, and HTTP-triggered jobs.</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching}
            className="rounded border border-gray-300 bg-white px-3 py-2 text-sm font-medium hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700"
            aria-label="Refresh scheduled jobs"
          >
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
          <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-100">
            <input
              type="checkbox"
              checked={autoRefreshEnabled}
              onChange={(event) => setAutoRefreshEnabled(event.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-700 focus:ring-2 focus:ring-primary-500 dark:border-gray-600"
            />
            Auto-refresh (every {AUTO_REFRESH_INTERVAL_SECONDS}s)
          </label>
        </div>
      </div>

      {isLoading ? (
        <div className="mt-4 card">
          <SkeletonTable rows={6} columns={6} />
        </div>
      ) : null}

      {!isLoading && isError ? (
        <div className="card mt-4">
          <h2 className="text-lg font-semibold text-red-700 dark:text-red-400">Unable to load scheduled jobs</h2>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">{error?.message || 'An unexpected error occurred while loading job data.'}</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-4 rounded border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
          >
            Try again
          </button>
        </div>
      ) : null}

      {!isLoading && !isError && jobs.length === 0 ? (
        <EmptyState
          title="No scheduled jobs found"
          message="No jobs were returned from the system monitoring API."
          action={
            <button
              type="button"
              onClick={() => void refetch()}
              className="rounded border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              Refresh
            </button>
          }
        />
      ) : null}

      {!isLoading && !isError && jobs.length > 0 ? (
        <>
          <JobsTableSection title="Timer Triggers" jobs={timerJobs} />
          <JobsTableSection title="Queue Triggers" jobs={queueJobs} />
          <JobsTableSection title="HTTP Triggers" jobs={httpJobs} />
        </>
      ) : null}
    </div>
  );
}
